import { useCallback, useState } from 'react';
import { apiFetchRaw } from '../api/client';
import { categoryOf } from './speechCategory';

const API_BASE = import.meta.env.VITE_API_BASE as string;

export type MediaKind = 'image' | 'music' | 'speech' | 'video' | 'document';

export interface DownloadableItem {
  id: string;
  signedUrl: string | null;
  status: 'submitted' | 'in_progress' | 'ready' | 'failed' | string;
  title?: string | null;
  koji_codename?: string | null;
  mime_type?: string | null;
  /** Speech-only — lets the helper derive the CATEGORY_ prefix. */
  mode?: string | null;
}

export interface MediaDownloadResult {
  /** How many files actually landed in the ZIP (server may have filtered
   *  out unready or missing rows). */
  delivered: number;
  /** `ids.length - delivered`. */
  skipped: number;
}

export interface UseMediaDownloadOptions {
  /** Where to POST the selection payload, e.g. '/api/tools/images/zip'. */
  zipEndpoint: string;
  kind: MediaKind;
}

export interface UseMediaDownloadResult {
  downloadOne: (item: DownloadableItem) => Promise<void>;
  downloadZip: (items: DownloadableItem[]) => Promise<MediaDownloadResult>;
  busy: boolean;
  error: string | null;
  clearError: () => void;
}

function absolute(url: string): string {
  return url.startsWith('http') ? url : `${API_BASE}${url}`;
}

function slugify(s: string, fallback: string): string {
  const base = (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return base || fallback;
}

function extensionFor(
  kind: MediaKind,
  mimeType: string | null | undefined,
): string {
  const mt = (mimeType ?? '').toLowerCase();
  if (kind === 'image') {
    if (mt === 'image/png') return 'png';
    if (mt === 'image/jpeg' || mt === 'image/jpg') return 'jpg';
    if (mt === 'image/webp') return 'webp';
    return 'bin';
  }
  if (kind === 'music') {
    // Callers pass a mime-ish hint derived from the `format` column.
    if (mt === 'audio/wav' || mt === 'audio/x-wav' || mt === 'wav') return 'wav';
    if (mt === 'audio/mpeg' || mt === 'audio/mp3' || mt === 'mp3') return 'mp3';
    if (mt === 'audio/ogg' || mt === 'ogg') return 'ogg';
    return 'bin';
  }
  if (kind === 'video') {
    if (mt === 'video/webm') return 'webm';
    return 'mp4';
  }
  if (kind === 'document') return 'md';
  return 'wav';
}

function prefixFor(kind: MediaKind, item: DownloadableItem): string {
  if (kind === 'speech') return categoryOf(item.mode ?? '');
  return kind;  // 'image' | 'music' | 'video' | 'document'
}

function singleFilename(kind: MediaKind, item: DownloadableItem): string {
  const slug = slugify(
    item.title || item.koji_codename || item.id,
    item.id.slice(0, 8),
  );
  const ext = extensionFor(kind, item.mime_type);
  return `${prefixFor(kind, item)}_${slug}.${ext}`;
}

function zipArchiveName(kind: MediaKind): string {
  const today = new Date().toISOString().slice(0, 10);
  const plural = kind === 'speech' ? 'speech-selection' : `${kind}s`;
  return `${plural}-${today}.zip`;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

/**
 * Stage 27: shared download helper for galleries. `downloadOne` fetches a
 * single signed R2 object and saves it locally with a kind-prefixed
 * filename; `downloadZip` POSTs the selected ids to the worker and saves
 * the returned ZIP. Errors are captured on `error` so the GalleryToolbar
 * can surface them without each gallery growing its own handler.
 */
export function useMediaDownload(
  opts: UseMediaDownloadOptions,
): UseMediaDownloadResult {
  const { zipEndpoint, kind } = opts;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  const downloadOne = useCallback(
    async (item: DownloadableItem): Promise<void> => {
      if (!item.signedUrl || item.status !== 'ready') {
        setError('File is not ready yet.');
        return;
      }
      setBusy(true);
      setError(null);
      try {
        // Stage post-37: anchor-navigation download instead of
        // fetch+blob. The signed URL points at our worker
        // (cross-origin from the firebase-hosted frontend). A fetch
        // would require Access-Control-Allow-Origin headers on the
        // response — the worker emits them, but stale browser
        // caches and some extensions can strip them, surfacing as a
        // mysterious "failed to fetch" with a CORS console error.
        // Anchor navigation bypasses CORS entirely (it's a
        // navigation, not a fetch). The worker's Content-Disposition
        // response (when `?dl=1` is present) instructs the browser
        // to save instead of navigate-and-render; the `?fn=` query
        // param carries the friendly filename.
        const filename = singleFilename(kind, item);
        const sep = item.signedUrl.includes('?') ? '&' : '?';
        const downloadUrl =
          absolute(item.signedUrl) +
          `${sep}dl=1&fn=${encodeURIComponent(filename)}`;
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = filename;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    },
    [kind],
  );

  const downloadZip = useCallback(
    async (items: DownloadableItem[]): Promise<MediaDownloadResult> => {
      if (items.length === 0) return { delivered: 0, skipped: 0 };
      setBusy(true);
      setError(null);
      try {
        const res = await apiFetchRaw(zipEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids: items.map((i) => i.id) }),
        });
        if (!res.ok) {
          let msg = `HTTP ${res.status}`;
          try {
            const body = (await res.json()) as { error?: string };
            if (body?.error) msg = body.error;
          } catch {
            /* non-JSON error body */
          }
          throw new Error(msg);
        }
        const blob = await res.blob();
        triggerDownload(blob, zipArchiveName(kind));
        const skippedHeader = res.headers.get('X-ZeroSynth-Skipped');
        const skipped = skippedHeader ? Number(skippedHeader) || 0 : 0;
        return { delivered: items.length - skipped, skipped };
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        setError(msg);
        return { delivered: 0, skipped: items.length };
      } finally {
        setBusy(false);
      }
    },
    [kind, zipEndpoint],
  );

  return { downloadOne, downloadZip, busy, error, clearError };
}
